import { Avatar } from '@billda/design-system';
import {
  VisibilityIcon,
  VisibilityOffIcon,
  PercentIcon,
  AttachMoneyIcon,
} from '@billda/design-system';
import { ListGangsResponse } from '../../api/gangsApi';

type GangMember = ListGangsResponse['gangs'][number]['members'][number];

interface GangMemberListItemProps {
  member: GangMember;
  itemPrefix: string;
  /** Semantic token classes for the avatar (surface + text). */
  avatarClassName: string;
  formatCurrency: (amount: number) => string;
}

const getInitials = (
  givenName?: string | null,
  familyName?: string | null
): string => {
  const first = (givenName ?? '').trim().charAt(0);
  const last = (familyName ?? '').trim().charAt(0);
  return first || last ? `${first}${last}`.toUpperCase() : 'U';
};

const getFullName = (member: GangMember): string => {
  const given = (member.givenName ?? '').trim();
  const family = (member.familyName ?? '').trim();
  return given || family ? `${given} ${family}`.trim() : 'Unknown';
};

export const GangMemberListItem = ({
  member,
  itemPrefix,
  avatarClassName,
  formatCurrency,
}: GangMemberListItemProps) => {
  const {
    defaultSplitPercentage,
    defaultRate,
    defaultUnitsValue,
    canSeeSplit,
  } = member;
  const hasSplit =
    defaultSplitPercentage !== null && defaultSplitPercentage !== undefined;
  const hasFixedRate = defaultRate !== null && defaultRate !== undefined;

  return (
    <li
      className="list-none pl-0"
      data-testid={`${itemPrefix}-item-${member.userId}`}
    >
      <div className="flex items-center gap-4">
        <Avatar
          aria-label={getFullName(member)}
          className={`size-8 ${avatarClassName}`}
          data-testid={`${itemPrefix}-avatar-${member.userId}`}
        >
          {getInitials(member.givenName, member.familyName)}
        </Avatar>
        <div className="min-w-0 flex-1">
          <span
            className="type-body-md block font-semibold"
            data-testid={`${itemPrefix}-name-${member.userId}`}
          >
            {getFullName(member)}
          </span>
          <span className="block">
            {canSeeSplit !== null && canSeeSplit !== undefined && (
              <span
                className="mt-0.5 flex items-center gap-0.5"
                data-testid={`${itemPrefix}-visibility-${member.userId}`}
                data-can-see-split={canSeeSplit ?? undefined}
              >
                {canSeeSplit ? (
                  <>
                    <VisibilityIcon
                      aria-hidden="true"
                      className="size-4 text-content-success"
                    />
                    <span className="type-caption text-content-success">
                      Can see splits
                    </span>
                  </>
                ) : (
                  <>
                    <VisibilityOffIcon
                      aria-hidden="true"
                      className="size-4 text-content-error"
                    />
                    <span className="type-caption text-content-error">
                      Cannot see splits
                    </span>
                  </>
                )}
              </span>
            )}

            {hasSplit || hasFixedRate ? (
              <span
                className="mt-0.5 flex items-center gap-2"
                data-testid={`${itemPrefix}-financial-${member.userId}`}
              >
                {hasSplit && (
                  <span
                    className="flex items-center gap-0.5"
                    data-testid={`${itemPrefix}-split-${member.userId}`}
                    data-percentage={
                      defaultSplitPercentage !== null &&
                      defaultSplitPercentage !== undefined
                        ? Math.round(defaultSplitPercentage * 1000) / 10
                        : undefined
                    }
                  >
                    <PercentIcon
                      aria-hidden="true"
                      className="size-4 text-content-accent"
                    />
                    <span className="type-caption text-content-accent">
                      {Math.round(defaultSplitPercentage! * 1000) / 10}% split
                    </span>
                  </span>
                )}

                {hasFixedRate && (
                  <span
                    className="flex items-center gap-0.5"
                    data-testid={`${itemPrefix}-fixedrate-${member.userId}`}
                    data-fixed-rate={defaultRate ?? undefined}
                    data-units-value={defaultUnitsValue ?? undefined}
                  >
                    <AttachMoneyIcon
                      aria-hidden="true"
                      className="size-4 text-content-secondary"
                    />
                    <span className="type-caption text-content-secondary">
                      {formatCurrency(defaultRate!)}
                      {defaultUnitsValue !== null &&
                      defaultUnitsValue !== undefined
                        ? ` × ${defaultUnitsValue} units`
                        : ''}
                    </span>
                  </span>
                )}
              </span>
            ) : null}
          </span>
        </div>
      </div>
    </li>
  );
};
